'use client';

/**
 * Service Taxonomy Selector Component
 *
 * Hierarchical service type selector supporting:
 * - Domain -> Category -> Service Type navigation
 * - Search across all service types
 * - Display of service details (units, duration, billing codes)
 *
 * Used in referral creation and service request workflows.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Search,
  ChevronRight,
  ChevronLeft,
  Check,
  Clock,
  FileText,
  Layers,
  AlertCircle,
  X,
  Loader2,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  serviceReferralsAPI,
  ServiceDomain,
  ServiceCategory,
  ServiceType,
  ServiceTypeSearchResult,
} from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceTaxonomySelectorProps {
  value?: string; // Selected service_type_code
  onChange: (serviceTypeCode: string, serviceType?: ServiceType) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  showDetails?: boolean; // Show service type details when selected
  allowSearch?: boolean; // Enable search functionality
  className?: string;
}

type SelectionStep = 'domain' | 'category' | 'type' | 'selected';

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceTaxonomySelector({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  showDetails = true,
  allowSearch = true,
  className = '',
}: ServiceTaxonomySelectorProps) {
  const { t } = useAppTranslation();
  const defaultPlaceholder = placeholder || t('serviceReferrals.taxonomy.selectServiceType', 'Select a service type');

  // State
  const [step, setStep] = useState<SelectionStep>('domain');
  const [domains, setDomains] = useState<ServiceDomain[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<ServiceDomain | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [selectedType, setSelectedType] = useState<ServiceType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ServiceTypeSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Load domains on mount
  useEffect(() => {
    loadDomains();
  }, []);

  // Load selected type if value is provided
  useEffect(() => {
    if (value && !selectedType) {
      loadSelectedType(value);
    }
  }, [value]);

  // Search debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await serviceReferralsAPI.searchServiceTypes(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // ========================================================================
  // DATA LOADING
  // ========================================================================

  const loadDomains = async () => {
    setLoading(true);
    try {
      const data = await serviceReferralsAPI.getServiceDomains();
      setDomains(data);
    } catch (err) {
      console.error('Failed to load domains:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async (domainId: number) => {
    setLoading(true);
    try {
      const data = await serviceReferralsAPI.getServiceCategories(domainId);
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadServiceTypes = async (categoryId: number) => {
    setLoading(true);
    try {
      const data = await serviceReferralsAPI.getServiceTypes(categoryId);
      setServiceTypes(data);
    } catch (err) {
      console.error('Failed to load service types:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedType = async (code: string) => {
    try {
      // Search for the type by code to get full details
      const results = await serviceReferralsAPI.searchServiceTypes(code, 1);
      if (results.length > 0) {
        // Get full type details
        const types = await serviceReferralsAPI.getServiceTypes();
        const fullType = types.find((t) => t.code === code);
        if (fullType) {
          setSelectedType(fullType);
          setStep('selected');
        }
      }
    } catch (err) {
      console.error('Failed to load selected type:', err);
    }
  };

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  const handleDomainSelect = (domain: ServiceDomain) => {
    setSelectedDomain(domain);
    setSelectedCategory(null);
    setSelectedType(null);
    loadCategories(domain.id);
    setStep('category');
  };

  const handleCategorySelect = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setSelectedType(null);
    loadServiceTypes(category.id);
    setStep('type');
  };

  const handleTypeSelect = (type: ServiceType) => {
    setSelectedType(type);
    setStep('selected');
    onChange(type.code, type);
    setIsOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchResultSelect = async (result: ServiceTypeSearchResult) => {
    // Get full type details
    try {
      const types = await serviceReferralsAPI.getServiceTypes();
      const fullType = types.find((t) => t.code === result.code);
      if (fullType) {
        handleTypeSelect(fullType);
      }
    } catch (err) {
      console.error('Failed to get type details:', err);
    }
  };

  const handleBack = () => {
    if (step === 'category') {
      setStep('domain');
      setSelectedDomain(null);
      setCategories([]);
    } else if (step === 'type') {
      setStep('category');
      setSelectedCategory(null);
      setServiceTypes([]);
    } else if (step === 'selected') {
      setStep('type');
      setSelectedType(null);
    }
  };

  const handleClear = () => {
    setSelectedDomain(null);
    setSelectedCategory(null);
    setSelectedType(null);
    setStep('domain');
    setSearchQuery('');
    setSearchResults([]);
    onChange('', undefined);
  };

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const renderDomainIcon = (iconName?: string) => {
    // Map icon names to Lucide icons
    const iconMap: Record<string, React.ReactNode> = {
      heart: <span className="text-red-500">❤️</span>,
      brain: <span className="text-purple-500">🧠</span>,
      home: <span className="text-blue-500">🏠</span>,
      briefcase: <span className="text-amber-500">💼</span>,
      utensils: <span className="text-green-500">🍴</span>,
      car: <span className="text-gray-500">🚗</span>,
      graduation: <span className="text-indigo-500">🎓</span>,
      shield: <span className="text-teal-500">🛡️</span>,
    };
    return iconMap[iconName || ''] || <Layers className="w-5 h-5 text-gray-400" />;
  };

  const renderSelectedDisplay = () => {
    if (!selectedType) {
      return (
        <div className="text-gray-500 flex items-center">
          {defaultPlaceholder}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500" />
          <span className="font-medium">{selectedType.name}</span>
          <Badge variant="outline" className="text-xs">
            {selectedType.code}
          </Badge>
        </div>
        {!disabled && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  };

  const renderServiceTypeDetails = () => {
    if (!selectedType || !showDetails) return null;

    return (
      <Card className="mt-2 bg-gray-50">
        <CardContent className="p-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('serviceReferrals.taxonomy.category', 'Category')}:</span>
              <span>{selectedType.category_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('serviceReferrals.taxonomy.domain', 'Domain')}:</span>
              <span>{selectedType.domain_name}</span>
            </div>
            {selectedType.default_units && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serviceReferrals.taxonomy.defaultUnits', 'Default Units')}:</span>
                <span>{selectedType.default_units} {selectedType.unit_type || 'units'}</span>
              </div>
            )}
            {selectedType.default_duration_days && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serviceReferrals.taxonomy.defaultDuration', 'Default Duration')}:</span>
                <span>{selectedType.default_duration_days} {t('serviceReferrals.common.days', 'days')}</span>
              </div>
            )}
            {selectedType.requires_prior_auth && (
              <div className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                <span>{t('serviceReferrals.taxonomy.requiresPriorAuth', 'Requires prior authorization')}</span>
              </div>
            )}
            {selectedType.description && (
              <div className="pt-2 border-t">
                <p className="text-gray-600">{selectedType.description}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div className={`relative ${className}`}>
      {/* Selected Value Display / Trigger */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          flex items-center min-h-[42px] px-3 py-2 border rounded-md cursor-pointer
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
          ${error ? 'border-red-500' : 'border-gray-200'}
        `}
      >
        {renderSelectedDisplay()}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {/* Service Type Details */}
      {selectedType && showDetails && renderServiceTypeDetails()}

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <Card className="absolute z-50 w-full mt-1 shadow-lg max-h-[400px] overflow-hidden">
          {/* Search Bar */}
          {allowSearch && (
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('serviceReferrals.taxonomy.searchPlaceholder', 'Search service types...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                )}
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchQuery && searchResults.length > 0 && (
            <div className="max-h-[300px] overflow-y-auto">
              {searchResults.map((result) => (
                <div
                  key={result.id}
                  onClick={() => handleSearchResultSelect(result)}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0"
                >
                  <div className="font-medium">{result.name}</div>
                  <div className="text-sm text-gray-500">
                    {result.domain_name} → {result.category_name}
                  </div>
                  <Badge variant="outline" className="mt-1 text-xs">
                    {result.code}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {/* Search No Results */}
          {searchQuery && searchResults.length === 0 && !isSearching && (
            <div className="p-4 text-center text-gray-500">
              {t('serviceReferrals.taxonomy.noResultsMatching', 'No service types found matching')} "{searchQuery}"
            </div>
          )}

          {/* Hierarchical Navigation */}
          {!searchQuery && (
            <div className="max-h-[300px] overflow-y-auto">
              {/* Breadcrumb / Back Button */}
              {step !== 'domain' && (
                <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleBack}>
                    <ChevronLeft className="w-4 h-4" />
                    {t('serviceReferrals.common.back', 'Back')}
                  </Button>
                  <span className="text-sm text-gray-500">
                    {selectedDomain?.name}
                    {selectedCategory && ` → ${selectedCategory.name}`}
                  </span>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="p-8 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              )}

              {/* Domains List */}
              {step === 'domain' && !loading && (
                <div>
                  {domains.map((domain) => (
                    <div
                      key={domain.id}
                      onClick={() => handleDomainSelect(domain)}
                      className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {renderDomainIcon(domain.icon_name)}
                        <div>
                          <div className="font-medium">{domain.name}</div>
                          {domain.description && (
                            <div className="text-sm text-gray-500 line-clamp-1">
                              {domain.description}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}

              {/* Categories List */}
              {step === 'category' && !loading && (
                <div>
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">{category.name}</div>
                        {category.description && (
                          <div className="text-sm text-gray-500 line-clamp-1">
                            {category.description}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      {t('serviceReferrals.taxonomy.noCategoriesFound', 'No categories found')}
                    </div>
                  )}
                </div>
              )}

              {/* Service Types List */}
              {step === 'type' && !loading && (
                <div>
                  {serviceTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => handleTypeSelect(type)}
                      className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{type.name}</div>
                        <Badge variant="outline" className="text-xs">
                          {type.code}
                        </Badge>
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                        {type.default_units && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {type.default_units} {type.unit_type || 'units'}
                          </span>
                        )}
                        {type.default_duration_days && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {type.default_duration_days} {t('serviceReferrals.common.days', 'days')}
                          </span>
                        )}
                        {type.requires_prior_auth && (
                          <Badge variant="outline" className="text-xs text-amber-600">
                            {t('serviceReferrals.taxonomy.priorAuth', 'Prior Auth')}
                          </Badge>
                        )}
                      </div>
                      {type.description && (
                        <div className="mt-1 text-sm text-gray-500 line-clamp-2">
                          {type.description}
                        </div>
                      )}
                    </div>
                  ))}
                  {serviceTypes.length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      {t('serviceReferrals.taxonomy.noServiceTypesFound', 'No service types found')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

export default ServiceTaxonomySelector;
